import { useEffect, useMemo, useRef } from 'react';
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
  forceX,
  forceY,
  type Simulation,
} from 'd3-force';
import type { SearchResult } from '../lib/types';
import { deriveGraph, type GraphEdge, type GraphNode } from '../lib/graph';

interface GraphViewProps {
  results: SearchResult[];
  activeSlug: string | null;
  hoverSlug: string | null;
  onSelect: (slug: string) => void;
  onHover: (slug: string | null) => void;
}

const WIDTH = 1600;
const HEIGHT = 520;
const MOUSE_RADIUS = 80;
const MOUSE_STRENGTH = 1.5;
const MOUSE_DEAD_ZONE = 40;
const ALPHA_IDLE = 0.025; // baseline continuous motion — never fully stop
const ALPHA_CURSOR = 0.07;
const ALPHA_DRAG = 0.3;

function nodeRadius(n: GraphNode): number {
  return n.isSeed ? 6 : 3.5;
}

export function GraphView({
  results,
  activeSlug,
  hoverSlug,
  onSelect,
  onHover,
}: GraphViewProps) {
  const graph = useMemo(() => deriveGraph(results), [results]);

  const svgRef = useRef<SVGSVGElement>(null);
  const edgeRefs = useRef<(SVGLineElement | null)[]>([]);
  const bloomRefs = useRef<(SVGGElement | null)[]>([]);
  const coreRefs = useRef<(SVGGElement | null)[]>([]);
  const simRef = useRef<Simulation<GraphNode, GraphEdge> | null>(null);
  const mouseRef = useRef<{ x: number | null; y: number | null }>({ x: null, y: null });
  const draggingRef = useRef<string | null>(null);

  useEffect(() => {
    if (graph.nodes.length === 0) {
      simRef.current?.stop();
      simRef.current = null;
      return;
    }

    graph.nodes.forEach((n) => {
      if (n.x == null) n.x = WIDTH / 2 + (Math.random() - 0.5) * 240;
      if (n.y == null) n.y = HEIGHT / 2 + (Math.random() - 0.5) * 200;
    });

    const sim = forceSimulation<GraphNode, GraphEdge>(graph.nodes)
      .force('charge', forceManyBody<GraphNode>().strength(-220))
      .force(
        'link',
        forceLink<GraphNode, GraphEdge>(graph.edges)
          .id((d) => d.id)
          .distance((l) => {
            const s = l.source as GraphNode;
            const t = l.target as GraphNode;
            return 80 + (s.isSeed || t.isSeed ? 20 : 0);
          })
          .strength(0.5),
      )
      .force('center', forceCenter<GraphNode>(WIDTH / 2, HEIGHT / 2).strength(0.04))
      .force('x', forceX<GraphNode>(WIDTH / 2).strength(0.05))
      .force('y', forceY<GraphNode>(HEIGHT / 2).strength(0.08))
      .force('collide', forceCollide<GraphNode>((d) => nodeRadius(d) * 3))
      .force('mouse', mouseForce)
      .alphaDecay(0.01)
      .velocityDecay(0.38)
      .alphaTarget(ALPHA_IDLE)
      .on('tick', updatePositions);

    simRef.current = sim;

    function mouseForce(alpha: number) {
      const { x: mx, y: my } = mouseRef.current;
      if (mx === null || my === null) return;
      const R = MOUSE_RADIUS;
      const DZ = MOUSE_DEAD_ZONE;
      for (const n of graph.nodes) {
        if (n.fx != null || n.fy != null) continue;
        const dx = (n.x ?? 0) - mx;
        const dy = (n.y ?? 0) - my;
        const d2 = dx * dx + dy * dy;
        if (d2 < DZ * DZ) continue;
        if (d2 < R * R && d2 > 1) {
          const d = Math.sqrt(d2);
          const f = (1 - d / R) * MOUSE_STRENGTH * alpha;
          n.vx = (n.vx ?? 0) + (dx / d) * f;
          n.vy = (n.vy ?? 0) + (dy / d) * f;
        }
      }
    }

    function updatePositions() {
      const MARGIN = 20;
      for (const n of graph.nodes) {
        if (n.x != null) n.x = Math.max(MARGIN, Math.min(WIDTH - MARGIN, n.x));
        if (n.y != null) n.y = Math.max(MARGIN, Math.min(HEIGHT - MARGIN, n.y));
      }
      for (let i = 0; i < graph.edges.length; i++) {
        const e = graph.edges[i];
        const line = edgeRefs.current[i];
        if (!line) continue;
        const s = e.source as GraphNode;
        const t = e.target as GraphNode;
        line.setAttribute('x1', String(s.x ?? 0));
        line.setAttribute('y1', String(s.y ?? 0));
        line.setAttribute('x2', String(t.x ?? 0));
        line.setAttribute('y2', String(t.y ?? 0));
      }
      for (let i = 0; i < graph.nodes.length; i++) {
        const n = graph.nodes[i];
        const x = n.x ?? 0;
        const y = n.y ?? 0;
        const transform = `translate(${x}, ${y})`;
        const bloom = bloomRefs.current[i];
        const core = coreRefs.current[i];
        if (bloom) bloom.setAttribute('transform', transform);
        if (core) core.setAttribute('transform', transform);
      }
    }

    return () => {
      sim.stop();
    };
  }, [graph]);

  useEffect(() => {
    for (let i = 0; i < graph.nodes.length; i++) {
      const g = coreRefs.current[i];
      if (!g) continue;
      const id = graph.nodes[i].id;
      g.classList.toggle('active', id === activeSlug);
      g.classList.toggle('hover', id === hoverSlug);
    }
    for (let i = 0; i < graph.edges.length; i++) {
      const e = graph.edges[i];
      const line = edgeRefs.current[i];
      if (!line) continue;
      const src = typeof e.source === 'object' ? (e.source as GraphNode).id : e.source;
      const tgt = typeof e.target === 'object' ? (e.target as GraphNode).id : e.target;
      const on =
        (activeSlug && (src === activeSlug || tgt === activeSlug)) ||
        (hoverSlug && (src === hoverSlug || tgt === hoverSlug));
      line.classList.toggle('active', !!on);
    }
  }, [graph, activeSlug, hoverSlug]);

  const pointerToSvg = (e: React.PointerEvent | React.MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const pt = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse());
    return { x: pt.x, y: pt.y };
  };

  const handleSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (draggingRef.current) return;
    const { x, y } = pointerToSvg(e);
    mouseRef.current.x = x;
    mouseRef.current.y = y;
    simRef.current?.alphaTarget(ALPHA_CURSOR).restart();
  };

  const handleSvgMouseLeave = () => {
    mouseRef.current.x = null;
    mouseRef.current.y = null;
    simRef.current?.alphaTarget(ALPHA_IDLE);
  };

  const handleNodePointerDown = (node: GraphNode) => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingRef.current = node.id;
    simRef.current?.alphaTarget(ALPHA_DRAG).restart();
    const { x, y } = pointerToSvg(e);
    node.fx = x;
    node.fy = y;
  };

  const handleNodePointerMove = (node: GraphNode) => (e: React.PointerEvent) => {
    if (draggingRef.current !== node.id) return;
    const { x, y } = pointerToSvg(e);
    node.fx = x;
    node.fy = y;
  };

  const handleNodePointerUp = (node: GraphNode) => (e: React.PointerEvent) => {
    if (draggingRef.current !== node.id) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // capture may already be released
    }
    node.fx = null;
    node.fy = null;
    draggingRef.current = null;
    simRef.current?.alphaTarget(ALPHA_IDLE);
  };

  if (graph.nodes.length === 0) return null;

  return (
    <div className="graph-stage">
      <svg
        ref={svgRef}
        className="graph-svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label="search result connection graph"
        onMouseMove={handleSvgMouseMove}
        onMouseLeave={handleSvgMouseLeave}
      >
        <g className="graph-bloom-layer">
          {graph.nodes.map((n, i) => {
            const r = nodeRadius(n);
            const shimmerDur = 4 + (i % 5);
            const shimmerDelay = -((i * 0.7) % 6);
            return (
              <g
                key={n.id}
                className={`graph-bloom ${n.isSeed ? 'seed' : ''}`}
                ref={(el) => {
                  bloomRefs.current[i] = el;
                }}
                style={
                  n.isSeed
                    ? ({
                        ['--shimmer-duration' as string]: `${shimmerDur}s`,
                        ['--shimmer-delay' as string]: `${shimmerDelay}s`,
                      } as React.CSSProperties)
                    : undefined
                }
              >
                <circle className="node-bloom" r={r * 1.3} />
              </g>
            );
          })}
        </g>

        <g className="graph-edge-layer">
          {graph.edges.map((_, i) => (
            <line
              key={i}
              className="graph-edge"
              ref={(el) => {
                edgeRefs.current[i] = el;
              }}
            />
          ))}
        </g>

        <g className="graph-node-layer">
          {graph.nodes.map((n, i) => {
            const r = nodeRadius(n);
            return (
              <g
                key={n.id}
                className={`graph-node ${n.isSeed ? 'seed' : ''}`}
                ref={(el) => {
                  coreRefs.current[i] = el;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(n.id);
                }}
                onMouseEnter={() => onHover(n.id)}
                onMouseLeave={() => onHover(null)}
                onPointerDown={handleNodePointerDown(n)}
                onPointerMove={handleNodePointerMove(n)}
                onPointerUp={handleNodePointerUp(n)}
              >
                <circle className="node-ring" r={r * 2.2} />
                <circle className="node-core" r={r} />
                <text className="node-label" x={0} y={-r - 8} textAnchor="middle">
                  {n.title}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}
